import React, { useState, useMemo } from 'react';
import { 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle2, 
  Search, 
  Layers, 
  TrendingUp, 
  PieChart as PieChartIcon, 
  FileSpreadsheet,
  Target
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  ComposedChart, 
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ReferenceLine,
  LabelList
} from 'recharts';
import { Folio } from '../../types.ts';
import { PeriodDataPoint } from '../KpisManager.tsx';

interface ReworksReportViewProps {
  folios: Folio[];
  periodDataList: PeriodDataPoint[];
  periodMode: 'DIAS' | 'SEMANAS' | 'MESES';
  clientName?: string | null;
}

export default function ReworksReportView({
  folios,
  periodDataList,
  periodMode,
  clientName
}: ReworksReportViewProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'ALL' | 'DEVOLUCION'>('ALL');
  const [sortField, setSortField] = useState<'folioNumber' | 'date' | 'pieces' | 'cause'>('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const rowsPerPage = 10;

  // Real-time synchronization listener for uploaded reworks
  React.useEffect(() => {
    const handleReload = () => {
      setReloadTrigger(prev => prev + 1);
    };
    window.addEventListener('rework_incidents_updated', handleReload);
    window.addEventListener('storage', handleReload);
    return () => {
      window.removeEventListener('rework_incidents_updated', handleReload);
      window.removeEventListener('storage', handleReload);
    };
  }, []);

  // Load authentic saved customer rework incidents from localStorage
  const savedIncidents = useMemo(() => {
    try {
      const raw = localStorage.getItem('sic_pro_rework_incidents') || localStorage.getItem('sic_pro_incidents');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.filter((inc: any) => 
            !inc.id?.startsWith('INC-2026-') && 
            !inc.id?.startsWith('INC-BMW-')
          );
        }
      }
    } catch (e) {
      console.warn('Error reading saved incidents:', e);
    }
    return [];
  }, [reloadTrigger]);

  // Extract all folios with customer reworks (retrabajos cliente)
  const devolucionFolioItems = useMemo(() => {
    const list: Array<{
      id: string;
      folioNumber: string;
      date: string;
      clientName: string;
      costCenterName: string;
      totalPieces: number;
      devolucionPieces: number;
      type: 'DEVOLUCION';
      cause: string;
      operator: string;
      status: string;
      folio?: Folio;
    }> = [];

    const processedFolioKeys = new Set<string>();

    // 1. Process incidents stored in localStorage that are DEVOLUCION (Retrabajo Cliente)
    savedIncidents.forEach((inc: any) => {
      // Check client filter
      if (clientName && clientName !== 'ALL') {
        const cName = (inc.clientName || '').toLowerCase();
        const target = clientName.toLowerCase();
        if (!cName.includes(target) && !target.includes(cName)) {
          return;
        }
      }

      if (inc.type === 'DEVOLUCION') {
        const pzas = Number(inc.affectedPieces) || 0;
        if (pzas > 0) {
          const fNum = inc.folioId || inc.folioNumber || inc.id?.replace(/^INC-/, '') || 'S/N';
          const incDate = inc.dateReported || inc.date || '';

          const key = `${fNum}_${incDate}_${pzas}`;
          processedFolioKeys.add(key);
          processedFolioKeys.add(String(fNum));
          processedFolioKeys.add(String(inc.folioId || ''));

          list.push({
            id: inc.id || `INC-DEV-${fNum}`,
            folioNumber: fNum,
            date: incDate || '2026-08-20T10:00:00Z',
            clientName: inc.clientName || clientName || 'BMW GROUP',
            costCenterName: inc.costCenterName || 'Sede San Luis Potosí - Planta BMW',
            totalPieces: pzas,
            devolucionPieces: pzas,
            type: 'DEVOLUCION',
            cause: inc.causeCategory 
              ? `${inc.causeCategory} - ${inc.causeDetail || ''}`
              : (inc.causeDetail || 'Incidencia de Retrabajo Cliente'),
            operator: inc.reportedBy || 'Recepción Planta / Calidad',
            status: inc.status || 'RESUELTO'
          });
        }
      }
    });

    // 2. Process folios that have DEVOLUCION in their history or status
    folios.forEach(f => {
      const fNum = String((f as any).folioNumber || f.id || '');
      const fId = String(f.id || '');

      if (processedFolioKeys.has(fNum) || processedFolioKeys.has(fId)) {
        return;
      }

      const devHistory = f.history?.find(h => h.stage === 'DEVOLUCION');
      const isDev = f.status === 'DEVOLUCION' || !!devHistory;

      if (isDev) {
        const totalPieces = f.items?.reduce((s, it) => s + (Number(it.quantity) || 0), 0) || 0;
        let devPieces = 0;

        if (devHistory?.returnedItems && devHistory.returnedItems.length > 0) {
          devPieces = devHistory.returnedItems.reduce((acc: number, curr: any) => acc + (Number(curr.quantityReturned) || 0), 0);
        } else if (devHistory?.affectedPieces && Number(devHistory.affectedPieces) > 0) {
          devPieces = Number(devHistory.affectedPieces);
        } else {
          const text = `${devHistory?.returnDetails || ''} ${devHistory?.notes || ''}`;
          const match = text.match(/(\d+)\s*(?:pzas?|piezas?)/i);
          if (match && Number(match[1]) > 0) {
            devPieces = Number(match[1]);
          } else if (text.includes('TODO EL FOLIO') && totalPieces > 0) {
            devPieces = totalPieces;
          } else {
            devPieces = totalPieces > 0 ? totalPieces : 1;
          }
        }

        if (devPieces > 0) {
          processedFolioKeys.add(fNum);
          processedFolioKeys.add(fId);

          list.push({
            id: f.id,
            folioNumber: fNum,
            date: devHistory?.timestamp || f.createdDate || f.date || '',
            clientName: f.clientName || clientName || 'BMW GROUP',
            costCenterName: f.costCenterName || 'Planta Cliente',
            totalPieces: totalPieces || devPieces,
            devolucionPieces: devPieces,
            type: 'DEVOLUCION',
            cause: devHistory?.returnDetails || devHistory?.notes || 'Devolución de material para reacondicionamiento',
            operator: devHistory?.operatorName || 'Inspector Calidad',
            status: f.status,
            folio: f
          });
        }
      }
    });

    return list;
  }, [folios, savedIncidents, clientName]);

  // Overall metrics: Total Entregadas vs Total Piezas en Devolución with 4% Goal
  const metrics = useMemo(() => {
    let totalDelivered = 0;
    let totalReceived = 0;
    let totalDevolucionPieces = 0;

    periodDataList.forEach(p => {
      totalDelivered += p.piecesDelivered;
      totalReceived += p.piecesReceived;
      totalDevolucionPieces += p.devolucionPieces;
    });

    // Ensure devolution pieces match authentic audit if period sum is 0
    const listDevolucionSum = devolucionFolioItems.reduce((s, it) => s + it.devolucionPieces, 0);
    if (totalDevolucionPieces === 0 && listDevolucionSum > 0) {
      totalDevolucionPieces = listDevolucionSum;
    }

    const devolucionFoliosCount = devolucionFolioItems.length;
    const totalFoliosCount = folios.length;

    // % Devolución = (Piezas en Devolución / Total Piezas Entregadas) * 100
    const devolucionRate = totalDelivered > 0 
      ? Number(((totalDevolucionPieces / totalDelivered) * 100).toFixed(1))
      : 0;

    const isGoalMet = devolucionRate <= 4.0;

    return {
      totalDelivered,
      totalDevolucionPieces,
      totalReceived,
      devolucionFoliosCount,
      totalFoliosCount,
      devolucionRate,
      isGoalMet
    };
  }, [periodDataList, devolucionFolioItems, folios]);

  // Chart data: Trend of % Devoluciones strictly based on authentic devolution pieces
  const chartData = useMemo(() => {
    return periodDataList.map(p => {
      // Find items matching this period strictly by date
      const periodDevolucionItems = devolucionFolioItems.filter(item => {
        if (!item.date) return false;
        const d = new Date(item.date);
        if (isNaN(d.getTime())) return false;
        
        const itemIso = d.toISOString().slice(0, 10);
        const startIso = p.startDate.toISOString().slice(0, 10);
        const endIso = p.endDate.toISOString().slice(0, 10);
        
        return (d >= p.startDate && d <= p.endDate) || (itemIso >= startIso && itemIso <= endIso);
      });

      const listPieces = periodDevolucionItems.reduce((sum, it) => sum + it.devolucionPieces, 0);
      const devolucionPieces = Math.max(p.devolucionPieces, listPieces);
      const piecesDelivered = p.piecesDelivered;

      // Devolucion % = (Piezas en Devolución / Total Piezas Entregadas) * 100
      const devolucionPercent = piecesDelivered > 0 
        ? Number(((devolucionPieces / piecesDelivered) * 100).toFixed(1))
        : 0;

      return {
        periodLabel: p.periodLabel,
        piecesDelivered,
        devolucionPieces,
        reworkPieces: devolucionPieces,
        devolucionPercent,
        metaPercent: 4.0
      };
    });
  }, [periodDataList, devolucionFolioItems]);

  // Dynamic Y-axis domain ensuring 4% line is comfortably displayed with headroom
  const maxDevolucionPercent = useMemo(() => {
    const maxVal = chartData.reduce((max, d) => Math.max(max, d.devolucionPercent), 0);
    return Math.max(6, Math.ceil((Math.max(maxVal, 4) + 1.5) / 2) * 2);
  }, [chartData]);

  // Causes Breakdown for Devoluciones
  const causesSummary = useMemo(() => {
    if (devolucionFolioItems.length === 0) {
      return [];
    }

    const map = new Map<string, number>();
    devolucionFolioItems.forEach(item => {
      let category = 'Decapado Incompleto';
      const c = item.cause.toLowerCase();
      if (c.includes('adher') || c.includes('pintura')) category = 'Pintura Residual / Adherencia';
      else if (c.includes('holl') || c.includes('ceniz') || c.includes('mancha')) category = 'Ceniza / Hollín Residual';
      else if (c.includes('golp') || c.includes('deform') || c.includes('dañ')) category = 'Daño Físico / Deformación';
      else if (c.includes('granalla')) category = 'Presencia de Granalla';
      else if (c.includes('óxid') || c.includes('oxid')) category = 'Oxidación Superficial';
      else if (c.includes('reclam') || c.includes('rechaz') || c.includes('devol')) category = 'Rechazo en Recibo Planta';
      else category = 'Inspección Visual / Otros';

      map.set(category, (map.get(category) || 0) + item.devolucionPieces);
    });

    return Array.from(map.entries())
      .map(([name, pieces]) => ({ name, pieces }))
      .sort((a, b) => b.pieces - a.pieces);
  }, [devolucionFolioItems]);

  // Filtered & Sorted Table
  const filteredList = useMemo(() => {
    let list = devolucionFolioItems;
    if (filterType !== 'ALL') {
      list = list.filter(it => it.type === filterType);
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(it => 
        it.folioNumber.toLowerCase().includes(q) ||
        it.clientName.toLowerCase().includes(q) ||
        it.cause.toLowerCase().includes(q) ||
        it.operator.toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      if (sortField === 'date') {
        const dA = new Date(a.date).getTime() || 0;
        const dB = new Date(b.date).getTime() || 0;
        return sortAsc ? dA - dB : dB - dA;
      }
      if (sortField === 'pieces') {
        return sortAsc ? a.devolucionPieces - b.devolucionPieces : b.devolucionPieces - a.devolucionPieces;
      }
      if (sortField === 'cause') {
        return sortAsc ? a.cause.localeCompare(b.cause) : b.cause.localeCompare(a.cause);
      }
      return sortAsc ? a.folioNumber.localeCompare(b.folioNumber) : b.folioNumber.localeCompare(a.folioNumber);
    });

    return list;
  }, [devolucionFolioItems, filterType, searchTerm, sortField, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(filteredList.length / rowsPerPage));
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredList.slice(start, start + rowsPerPage);
  }, [filteredList, currentPage]);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* SUMMARY KPI CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* 1. Tasa de Retrabajos de Cliente con Meta del 4% */}
        <div className={`bg-[#242624] border rounded-2xl p-4 shadow-md ${
          metrics.isGoalMet 
            ? 'border-emerald-500/40 bg-gradient-to-b from-emerald-950/20 to-transparent' 
            : 'border-red-500/40 bg-gradient-to-b from-red-950/20 to-transparent'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Tasa de Retrabajos Cliente</span>
            <Target className={`h-4 w-4 ${metrics.isGoalMet ? 'text-emerald-400' : 'text-red-400'}`} />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono flex items-baseline gap-2">
            <span className={metrics.isGoalMet ? 'text-emerald-400' : 'text-red-400'}>{metrics.devolucionRate.toFixed(1)}%</span>
            <span className="text-[10px] text-emerald-400 font-bold bg-emerald-500/20 px-1.5 py-0.5 rounded border border-emerald-500/30">
              Meta ≤ 4.0%
            </span>
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            {metrics.totalDevolucionPieces.toLocaleString()} pzas en retrabajo vs {metrics.totalDelivered.toLocaleString()} entregadas
          </div>
        </div>

        {/* 2. Total Piezas en Retrabajo Cliente */}
        <div className="bg-[#242624] border border-red-500/40 rounded-2xl p-4 shadow-md bg-gradient-to-b from-red-950/20 to-transparent">
          <div className="flex items-center justify-between text-red-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-red-300">Piezas Retrabajo Cliente</span>
            <RefreshCw className="h-4 w-4 text-red-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-red-400 font-mono">
            {metrics.totalDevolucionPieces.toLocaleString()}
          </div>
          <div className="text-[10px] text-red-300/80 mt-1 truncate">
            Piezas con retrabajo reportado por cliente
          </div>
        </div>

        {/* 3. Folios con Retrabajo Cliente */}
        <div className="bg-[#242624] border border-[#3a3d3a] rounded-2xl p-4 shadow-md">
          <div className="flex items-center justify-between text-amber-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Folios con Retrabajo Cliente</span>
            <AlertTriangle className="h-4 w-4 text-amber-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono">
            {metrics.devolucionFoliosCount} <span className="text-xs font-normal text-slate-400">/ {metrics.totalFoliosCount}</span>
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            Folios con piezas en retrabajo cliente
          </div>
        </div>

        {/* 4. Total Piezas Entregadas */}
        <div className="bg-[#242624] border border-blue-500/30 rounded-2xl p-4 shadow-md bg-gradient-to-b from-blue-950/20 to-transparent">
          <div className="flex items-center justify-between text-blue-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-300">Total Facturado (Entregado)</span>
            <Layers className="h-4 w-4 text-blue-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-blue-400 font-mono">
            {metrics.totalDelivered.toLocaleString()}
          </div>
          <div className="text-[10px] text-blue-300/80 mt-1 truncate">
            Base para cálculo de meta 4%
          </div>
        </div>
      </div>

      {/* CHART & PARETO SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Trend Chart */}
        <div className="lg:col-span-2 bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-[#343634]">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-rose-400" />
                <span>Porcentaje de Retrabajos Cliente (% Retrabajos)</span>
              </h3>
              <p className="text-xs text-slate-400">
                Porcentaje de piezas con retrabajo de cliente sobre el total de piezas entregadas. Meta institucional: ≤ 4.0%.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="h-2.5 w-4 bg-rose-500 rounded-full" />
                <span className="text-rose-400 font-bold">% Retrabajo Cliente</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-0.5 w-4 border-t-2 border-dashed border-emerald-400" />
                <span className="text-emerald-400 font-bold">Meta 4.0%</span>
              </div>
            </div>
          </div>

          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 25, right: 35, left: -10, bottom: 15 }}>
                <defs>
                  <linearGradient id="devolucionPercentGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.25}/>
                    <stop offset="95%" stopColor="#f43f5e" stopOpacity={0.0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#343634" opacity={0.6} />
                <XAxis dataKey="periodLabel" stroke="#8e918e" fontSize={11} tickLine={false} dy={6} />
                
                {/* Single Y-Axis: Only Retrabajo Percentage */}
                <YAxis 
                  stroke="#8e918e" 
                  fontSize={11} 
                  tickLine={false} 
                  domain={[0, maxDevolucionPercent]}
                  tickFormatter={v => `${v}%`} 
                />

                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-[#181918] border border-[#3e413e] rounded-xl p-3 shadow-2xl space-y-1.5 text-xs">
                          <div className="flex items-center justify-between gap-4 border-b border-[#303330] pb-1">
                            <span className="font-bold text-white">{label}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              data.devolucionPercent <= 4.0 
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                : 'bg-red-500/20 text-red-400 border border-red-500/30'
                            }`}>
                              {data.devolucionPercent <= 4.0 ? 'CUMPLE META ≤ 4%' : 'SUPERA META > 4%'}
                            </span>
                          </div>
                          <div className="text-rose-400 pt-1 flex justify-between gap-3 font-bold text-sm">
                            <span>% Retrabajo Cliente:</span>
                            <span className="font-mono">{data.devolucionPercent.toFixed(1)}%</span>
                          </div>
                          <div className="text-[11px] text-slate-400 pt-1 border-t border-[#2d2f2d] flex justify-between gap-3">
                            <span>Piezas en Retrabajo:</span>
                            <span className="font-mono text-white">{data.devolucionPieces.toLocaleString()} pzas</span>
                          </div>
                          <div className="text-[11px] text-slate-400 flex justify-between gap-3">
                            <span>Total Facturado/Entregado:</span>
                            <span className="font-mono text-white">{data.piecesDelivered.toLocaleString()} pzas</span>
                          </div>
                          <div className="text-[11px] text-emerald-400 flex justify-between gap-3">
                            <span>Meta Institucional:</span>
                            <span className="font-mono font-bold">≤ 4.0%</span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />

                <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 10, fontSize: 11 }} />

                {/* Reference Line at 4% */}
                <ReferenceLine 
                  y={4.0} 
                  stroke="#10b981" 
                  strokeDasharray="4 4" 
                  strokeWidth={2} 
                  label={{ 
                    value: 'META 4%', 
                    position: 'insideTopRight', 
                    fill: '#10b981', 
                    fontSize: 11, 
                    fontWeight: 'bold' 
                  }} 
                />

                {/* Area & Line for % de Retrabajos with percentage data labels */}
                <Area 
                  type="monotone" 
                  dataKey="devolucionPercent" 
                  name="% Retrabajo Cliente" 
                  stroke="#f43f5e" 
                  strokeWidth={2.5} 
                  fill="url(#devolucionPercentGradient)"
                  dot={{ r: 5, fill: '#f43f5e', stroke: '#1b1c1b', strokeWidth: 2 }}
                  activeDot={{ r: 8, fill: '#fecdd3', stroke: '#be123c', strokeWidth: 2 }}
                >
                  <LabelList 
                    dataKey="devolucionPercent" 
                    position="top" 
                    offset={10} 
                    formatter={(val: any) => `${Number(val).toFixed(1)}%`} 
                    style={{ fill: '#f43f5e', fontSize: 11, fontWeight: 'bold' }} 
                  />
                </Area>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right Col: Causes Breakdown */}
        <div className="bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
          <div className="pb-2 border-b border-[#343634]">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <PieChartIcon className="h-5 w-5 text-amber-400" />
              <span>Distribución de Causas de Retrabajo Cliente</span>
            </h3>
            <p className="text-xs text-slate-400">
              Pareto de motivos principales de retrabajo reportados por cliente.
            </p>
          </div>

          {causesSummary.length === 0 ? (
            <div className="py-8 px-4 text-center text-slate-400 space-y-2.5">
              <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto opacity-80" />
              <p className="text-xs font-semibold text-slate-200">Sin retrabajos cliente registrados</p>
              <p className="text-[11px] text-slate-400 leading-relaxed max-w-xs mx-auto">
                No se tienen retrabajos de cliente ingresados en el periodo actual. La gráfica y métricas se mantienen al 0.0% con cumplimiento al 100%.
              </p>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              {causesSummary.map((c, i) => {
                const pct = metrics.totalDevolucionPieces > 0 ? (c.pieces / metrics.totalDevolucionPieces) * 100 : 0;
                return (
                  <div key={i} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-slate-200 truncate pr-2">{c.name}</span>
                      <span className="font-mono text-red-400 font-bold whitespace-nowrap">{c.pieces} pzs ({pct.toFixed(0)}%)</span>
                    </div>
                    <div className="w-full bg-[#161716] h-2 rounded-full overflow-hidden border border-[#343634]">
                      <div 
                        className="h-full bg-gradient-to-r from-amber-500 to-red-500 rounded-full" 
                        style={{ width: `${Math.min(100, Math.max(5, pct))}%` }} 
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* DETAILED RETRABAJOS CLIENTE TABLE */}
      <div className="bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-[#343634]">
          <div>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-brand-red" />
              <span>Bitácora de Folios con Retrabajo Cliente</span>
            </h3>
            <p className="text-xs text-slate-400">
              Auditoría detallada con causa, piezas en retrabajo cliente y responsable operativo.
            </p>
          </div>

          {/* Filters & Search */}
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                placeholder="Buscar folio, causa..."
                className="bg-[#161716] border border-[#3e413e] rounded-xl pl-8 pr-3 py-1.5 text-xs text-white placeholder:text-slate-500 focus:border-brand-red focus:outline-none"
              />
            </div>
            <select
              value={filterType}
              onChange={e => { setFilterType(e.target.value as any); setCurrentPage(1); }}
              className="bg-[#161716] border border-[#3e413e] rounded-xl px-3 py-1.5 text-xs text-white focus:border-brand-red focus:outline-none cursor-pointer"
            >
              <option value="ALL">Todos los Retrabajos Cliente</option>
              <option value="DEVOLUCION">Retrabajo Cliente confirmado</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-[#3a3d3a] text-slate-400 uppercase tracking-wider text-[11px] bg-[#1a1c1a]">
                <th className="px-3.5 py-3">Folio</th>
                <th className="px-3 py-3">Cliente / Destino</th>
                <th className="px-3 py-3 text-center">Tipo</th>
                <th className="px-3 py-3 text-center">Piezas Retrabajo</th>
                <th className="px-3.5 py-3">Motivo / Causa de Retrabajo Cliente</th>
                <th className="px-3 py-3">Reportado Por</th>
                <th className="px-3 py-3 text-center">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a2d2a]">
              {paginatedList.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    No se encontraron registros de retrabajos cliente para los filtros actuales.
                  </td>
                </tr>
              ) : (
                paginatedList.map(item => (
                  <tr key={item.id} className="hover:bg-[#242624] transition-colors">
                    <td className="px-3.5 py-3 font-mono font-bold text-white whitespace-nowrap">
                      {item.folioNumber}
                    </td>
                    <td className="px-3 py-3 text-slate-300">
                      <div>{item.clientName}</div>
                      <div className="text-[10px] text-slate-500">{item.costCenterName}</div>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/40">
                        RETRABAJO CLIENTE
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center font-mono font-bold text-red-400">
                      {item.devolucionPieces.toLocaleString()} pzs
                    </td>
                    <td className="px-3.5 py-3 text-slate-300 max-w-xs truncate">
                      {item.cause}
                    </td>
                    <td className="px-3 py-3 text-slate-400">
                      {item.operator}
                    </td>
                    <td className="px-3 py-3 text-center text-slate-400 whitespace-nowrap font-mono">
                      {item.date ? new Date(item.date).toLocaleDateString('es-MX') : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-slate-400">
          <div>
            Mostrando <span className="font-bold text-white">{filteredList.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}</span> a <span className="font-bold text-white">{Math.min(currentPage * rowsPerPage, filteredList.length)}</span> de <span className="font-bold text-white">{filteredList.length}</span> devoluciones
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 bg-[#161716] hover:bg-[#343634] text-slate-300 hover:text-white border border-[#3e413e] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Anterior
            </button>
            <span className="px-2 font-mono">Pág. {currentPage} de {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 bg-[#161716] hover:bg-[#343634] text-slate-300 hover:text-white border border-[#3e413e] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
